import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';

export function MarketingPopupTab({ content, setContent }) {
  const config = content.marketing_popup || {};

  const update = (field, value) => {
    setContent(prev => ({
      ...prev,
      marketing_popup: { ...(prev.marketing_popup || {}), [field]: value },
    }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Marketing Popup</CardTitle>
        <CardDescription>
          Show a delayed offer popup to landing page visitors. Uses the same look-and-feel as the cookie banner and
          adapts to light/dark theme automatically.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enable */}
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-base font-semibold">Enable Marketing Popup</Label>
            <p className="text-sm text-gray-500">Off by default. Turn on once your offer is ready.</p>
          </div>
          <Switch
            checked={config.enabled === true}
            onCheckedChange={(checked) => update('enabled', checked)}
            data-testid="marketing-popup-enabled-toggle"
          />
        </div>

        {/* Trigger */}
        <div className="space-y-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
          <Label className="text-base font-semibold">Trigger</Label>
          <p className="text-xs text-gray-600 -mt-1">How and when the popup appears. You can use either trigger or both.</p>

          <div className="flex items-center justify-between gap-4">
            <div>
              <Label className="text-sm font-medium">Time-based trigger</Label>
              <p className="text-xs text-gray-500">Show after the delay set below.</p>
            </div>
            <Switch
              checked={config.trigger_on_delay !== false}
              onCheckedChange={(checked) => update('trigger_on_delay', checked)}
              data-testid="marketing-popup-trigger-delay-toggle"
            />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <Label className="text-sm font-medium">Scroll-to-bottom trigger</Label>
              <p className="text-xs text-gray-500">Show when visitor reaches the bottom of the page (great for capturing readers who reached the end).</p>
            </div>
            <Switch
              checked={config.trigger_on_scroll === true}
              onCheckedChange={(checked) => update('trigger_on_scroll', checked)}
              data-testid="marketing-popup-trigger-scroll-toggle"
            />
          </div>

          {config.trigger_on_scroll && (
            <div className="pl-1">
              <Label>Scroll threshold (%)</Label>
              <Input
                type="number"
                min={50}
                max={100}
                value={config.scroll_threshold_pct ?? 90}
                onChange={(e) => update('scroll_threshold_pct', parseInt(e.target.value) || 90)}
                data-testid="marketing-popup-scroll-threshold-input"
                className="max-w-[140px]"
              />
              <p className="text-xs text-gray-500 mt-1">Fires when the user has scrolled this percentage of the page. Default 90% (just before the absolute bottom). Min 50, max 100.</p>
            </div>
          )}
        </div>

        {/* Timing */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Delay before showing (seconds)</Label>
            <Input
              type="number"
              min={3}
              max={600}
              value={config.delay_seconds ?? 20}
              onChange={(e) => update('delay_seconds', parseInt(e.target.value) || 20)}
              data-testid="marketing-popup-delay-input"
            />
            <p className="text-xs text-gray-500 mt-1">Default 20s. Min 3s, max 600s.</p>
          </div>
          <div>
            <Label>Suppress for (days) after dismissed</Label>
            <Input
              type="number"
              min={0}
              max={365}
              value={config.reset_days ?? 7}
              onChange={(e) => update('reset_days', parseInt(e.target.value) || 0)}
              data-testid="marketing-popup-reset-days-input"
            />
            <p className="text-xs text-gray-500 mt-1">If a visitor closes the popup, don't show again for this many days. Set to <strong>0</strong> for testing — popup will reappear on every page reload after the delay.</p>
          </div>
        </div>

        {/* Heading */}
        <div>
          <Label>Heading</Label>
          <Input
            value={config.heading || ''}
            onChange={(e) => update('heading', e.target.value)}
            placeholder="e.g., Special Offer Just For You"
            data-testid="marketing-popup-heading-input"
          />
        </div>

        {/* Description */}
        <div>
          <Label>Description</Label>
          <Textarea
            rows={3}
            value={config.description || ''}
            onChange={(e) => update('description', e.target.value)}
            placeholder="e.g., Sign up today and save 20% on your first month."
            data-testid="marketing-popup-description-input"
          />
        </div>

        {/* Discount code */}
        <div>
          <Label>Discount Code (shown to user, copyable)</Label>
          <Input
            value={config.code || ''}
            onChange={(e) => update('code', e.target.value)}
            placeholder="e.g., LAUNCH20"
            className="font-mono tracking-wider"
            data-testid="marketing-popup-code-input"
          />
          <p className="text-xs text-gray-500 mt-1">Leave blank to hide the code panel and just show the offer text.</p>
        </div>

        {/* Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Claim Button Text</Label>
            <Input
              value={config.button_text || ''}
              onChange={(e) => update('button_text', e.target.value)}
              placeholder="e.g., Claim Offer"
              data-testid="marketing-popup-button-text-input"
            />
          </div>
          <div>
            <Label>Dismiss Button Text</Label>
            <Input
              value={config.dismiss_text || ''}
              onChange={(e) => update('dismiss_text', e.target.value)}
              placeholder="e.g., Maybe later"
              data-testid="marketing-popup-dismiss-text-input"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
